import { BrowserBroadcast } from '@adapters/broadcast/browserBroadcast';
import { WebauthPopupPage } from '@adapters/navigation/page';
import { Logger } from '@core/model/domain/logger';
import { Option } from '@core/model/framework/Result/Option';
import { PromiseTimedOut, promiseWithTimeout } from '@core/model/framework/promiseWithTimeout';
import { SaveWebauthBankConnection } from '@core/usecase/saveWebauthBankConnection';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';

export class WebauthPopupVM {
  private HAS_USER_STOPPED_WAITING_CHECK_TIMEOUT_IN_MS = 5 * 1000;

  public webauthPopupActualPage: Option<WebauthPopupPage>;

  constructor(
    private saveBankConnectionUsecase: SaveWebauthBankConnection,
    private browserBroadcast: BrowserBroadcast,
    private loggerService: Logger
  ) {
    this.webauthPopupActualPage = null;
  }

  /*
    Server-callback flow: the backend already completed the connection and
    redirected here with ?outcome=... The page that started the journey
    resumes it by polling its connections — this page only reports the result
    on whichever device it runs on (the origin tab, or the phone after a qr
    scan). It never resumes the journey itself: coordination between contexts
    goes through the server, never through a browser inter-window channel
    (postMessage/opener are not usable across webviews).
  */
  handleServerOutcome(outcome: 'success' | 'failure'): void {
    if (outcome === 'success') {
      this.dispatchWebauthSuccess();
      return;
    }

    this.dispatchWebauthFail(new Error('WebauthPopupVM: server-side webauth completion failed'));
  }

  async onInit(webauthPopupConnectionId: string, webauthPopupUserToken: string) {
    this.browserBroadcast.publish({
      label: 'WEBAUTH_POPUP_REACHED',
      data: null,
    });

    const isWebauthCancelled = await this.hasUserStoppedWaiting();
    if (isWebauthCancelled) {
      this.dispatchWebauthFail(new Error('WebauthPopupVM: webauth has been cancelled'));
      return;
    }

    await this.saveBankConnection(webauthPopupUserToken, webauthPopupConnectionId);
  }

  // User can go back to the bank choice or maybe have two webauth tabs, and complete both.
  // In either of these cases, we do not want to save the connection and we will present
  // The fail screen.
  private async hasUserStoppedWaiting(): Promise<boolean> {
    const promise = () =>
      firstValueFrom(
        this.browserBroadcast.getMessageFromLabel('WEBAUTH_POPUP_ANSWER_HAS_USER_STOPPED_WAITING')
      );

    try {
      const response = await promiseWithTimeout(promise(), this.HAS_USER_STOPPED_WAITING_CHECK_TIMEOUT_IN_MS);
      return response.data.hasUserStoppedWaiting;
    } catch (e) {
      if (e instanceof PromiseTimedOut) {
        return false;
      }
      throw e;
    }
  }

  private async saveBankConnection(webauthPopupUserToken: string, webauthPopupConnectionId: string) {
    try {
      await this.saveBankConnectionUsecase.execute({
        webauthPopupUserToken,
        webauthPopupConnectionId,
      });
      this.dispatchWebauthSuccess();
    } catch (_e) {
      this.dispatchWebauthFail(new Error('WebauthPopupVM: save bank connection failed'));
    }
  }

  private dispatchWebauthSuccess() {
    this.webauthPopupActualPage = { value: 'CONNECTION_EXTERNAL_BANK_OK' };
  }

  dispatchWebauthFail(error: Error) {
    this.loggerService.logException(error);
    this.webauthPopupActualPage = { value: 'CONNECTION_EXTERNAL_BANK_KO' };
    this.browserBroadcast.publish({ label: 'CONNECTION_EXTERNAL_BANK_KO', data: null });
  }
}
